import React from 'react';
import { Users, Building, Crown, Sparkles, TreePine, Archive, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { propertyData } from '../../data/mock';

const ExpansionSection = ({ onInquire }) => {
  const getIcon = (title) => {
    if (title.includes('Family')) return Users;
    if (title.includes('Corporate')) return Building;
    if (title.includes('Heir')) return Crown;
    if (title.includes('Hospitality')) return Sparkles;
    if (title.includes('Equestrian')) return TreePine;
    return Archive;
  };

  return (
    <section id="estate" className="py-24 bg-[#0d1117] relative overflow-hidden" data-testid="expansion-section">
      {/* Background Decorations */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-[128px]" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-[128px]" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-1.5 bg-amber-500/10 border border-amber-500/30 rounded-full text-amber-400 text-sm font-medium tracking-wide mb-4">
            Future Potential
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-white mb-6">
            {propertyData.expansionCapability.title}
          </h2>
          <p className="text-lg text-white/70 font-light leading-relaxed">
            {propertyData.expansionCapability.description}
          </p>
        </div>

        {/* Build Rights Highlight */}
        <div className="max-w-4xl mx-auto mb-16">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-amber-500/20 to-amber-600/20 rounded-3xl blur-xl" />
            <div className="relative bg-gradient-to-br from-[#1a1f2e] to-[#0d1117] border-2 border-amber-500/40 rounded-3xl p-8 md:p-10">
              <div className="grid md:grid-cols-4 gap-6 text-center">
                <div className="md:col-span-1">
                  <div className="w-20 h-20 mx-auto rounded-2xl bg-amber-500/20 border border-amber-500/40 flex items-center justify-center mb-4">
                    <span className="text-4xl font-bold text-amber-400">1</span>
                  </div>
                  <p className="text-white/60 text-sm">Residence per</p>
                  <p className="text-white font-semibold">5 Acres</p>
                </div>
                <div className="md:col-span-1 flex items-center justify-center">
                  <ArrowRight className="w-8 h-8 text-amber-400/50 hidden md:block" />
                  <div className="h-px w-full bg-amber-400/30 md:hidden" />
                </div>
                <div className="md:col-span-2">
                  <div className="bg-white/5 rounded-2xl p-6">
                    <p className="text-4xl md:text-5xl font-bold text-amber-400 mb-2">4</p>
                    <p className="text-white text-lg font-medium">Total Residences Possible</p>
                    <p className="text-white/60 text-sm mt-2">Plus tiny homes or metal buildings</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Use Cases Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {propertyData.expansionCapability.useCases.map((useCase, index) => {
            const Icon = getIcon(useCase.title);
            return (
              <div
                key={index}
                className="group relative bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-2xl p-6 hover:border-amber-500/40 transition-all duration-500 hover:transform hover:-translate-y-1"
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500/10 to-amber-600/10 border border-amber-500/30 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                  <Icon className="w-7 h-7 text-amber-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{useCase.title}</h3>
                <p className="text-white/60 font-light">{useCase.description}</p>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <p className="text-white/70 mb-6 font-light">
            Discover how this estate can accommodate your vision
          </p>
          <Button
            onClick={onInquire}
            size="lg"
            className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-10 py-6 text-lg shadow-2xl shadow-amber-500/30"
            data-testid="expansion-inquire-btn"
          >
            Discuss the Opportunity
          </Button>
        </div>

        {/* Disclaimer */}
        <p className="text-center text-white/40 text-xs mt-8">
          {propertyData.expansionCapability.disclaimer}
        </p>
      </div>
    </section>
  );
};

export default ExpansionSection;
